import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Button, TextField, Select, MenuItem, FormControl, InputLabel, IconButton, Tooltip, Chip, useMediaQuery } from '@mui/material';
import { Delete, Add, Edit, Visibility, VisibilityOff, Summarize, ExpandMore, ChevronRight, UnfoldLess, UnfoldMore, UploadFile } from '@mui/icons-material';
import { Account, CashFlowCategory, PostingClass, SegmentFieldPolicy } from '../../interfaces/accounting.interface';
import { defaultPostingClass, postingClassLabel } from '../../utils/accountPostingClass';
import { AccountService } from '../../services/account.service';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import ScrollableTable from '../common/ScrollableTable';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { humanizeEnum } from '../../utils/format';
import {
    accountLevel, isDescendantAccount, sigPrefix as sigPrefixOf,
    suggestNextChildCode, validateAccountCode,
} from '../../utils/accountHierarchy';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import CoaImportDialog from './CoaImportDialog';

const tableHeaderStyle = {
    padding: '12px',
    textAlign: 'left' as const,
    backgroundColor: '#f5f5f5',
    borderBottom: '1px solid #ddd'
};

const tableCellStyle = {
    padding: '12px',
    borderBottom: '1px solid #ddd'
};

const modalStyle = {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 'min(500px, 92vw)',
    maxHeight: '90vh',
    overflowY: 'auto' as const,
    boxSizing: 'border-box' as const,
    backgroundColor: '#fff',
    boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
    padding: '32px',
    borderRadius: '8px'
};

const ACCOUNT_TYPES: Account['accountType'][] = ['ASSET', 'LIABILITY', 'EQUITY', 'REVENUE', 'EXPENSE'];

export const deriveSegmentPolicy = (type: Account['accountType']): SegmentFieldPolicy =>
    (type === 'REVENUE' || type === 'EXPENSE') ? 'REQUIRED' : 'OPTIONAL';

const CASH_FLOW_CATEGORY_OPTIONS: CashFlowCategory[] = ['CASH_EQUIVALENT', 'OPERATING', 'INVESTING', 'FINANCING', 'NON_CASH'];

const POSTING_CLASS_OPTIONS: PostingClass[] = [
    'BALANCE_SHEET', 'RECEIVABLE', 'PAYABLE', 'CASH', 'TAX',
    'EXPENSE', 'REVENUE', 'INVENTORY', 'GR_IR_CLEARING', 'INTER_SEGMENT_CLEARING',
    'INTER_SEGMENT_RECEIVABLE', 'INTER_SEGMENT_PAYABLE', // W3-C
];

const sigPrefix = sigPrefixOf;
const isDescendant = isDescendantAccount;
const getAccountLevel = accountLevel;

const ChartOfAccounts: React.FC = () => {
    // Server guards: create/update/hide = ACCOUNT_MANAGE, delete = ACCOUNTING_SETTINGS_MANAGE (admin-only).
    const canManageAccounts = usePermission(PERMISSIONS.ACCOUNT_MANAGE);
    const canDeleteAccounts = usePermission(PERMISSIONS.ACCOUNTING_SETTINGS_MANAGE);
    const navigate = useNavigate();
    const isNarrow = useMediaQuery('(max-width:600px)');
    const toast = useToast();
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [hiddenAccountIds, setHiddenAccountIds] = useState<Set<string>>(new Set());
    const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
    const [showHidden, setShowHidden] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isImportOpen, setIsImportOpen] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
    // A new account's posting class follows its type / parent until the user picks one themselves.
    const [postingClassTouched, setPostingClassTouched] = useState(false);
    const [formData, setFormData] = useState<{
        code: string;
        name: string;
        accountType: Account['accountType'];
        description: string;
        parentAccountId: string;
        postingClass: PostingClass | '';
        segmentFieldPolicy: SegmentFieldPolicy;
        cashFlowCategory: CashFlowCategory | '';
    }>({
        code: '',
        name: '',
        accountType: 'ASSET',
        description: '',
        parentAccountId: '',
        postingClass: '',
        segmentFieldPolicy: 'OPTIONAL',
        cashFlowCategory: ''
    });

    const fetchAccounts = async () => {
        try {
            setLoading(true);
            // Every page: the server caps a page at 200 rows, so a single call hides the rest.
            const data = await AccountService.getAllAccounts(true);
            setAccounts(Array.isArray(data) ? data : []);
        } catch (err) {
            toast.showApiError(err, 'Failed to load accounts');
        } finally {
            setLoading(false);
        }
    };

    const fetchHiddenAccounts = async () => {
        try {
            const hidden = await AccountService.getHiddenAccounts();
            setHiddenAccountIds(new Set(hidden));
        } catch (err) {
            console.error('Error fetching hidden accounts:', err);
        }
    };

    useEffect(() => {
        fetchAccounts();
        fetchHiddenAccounts();
    }, []);

    const handleToggleVisibility = async (account: Account) => {
        try {
            if (hiddenAccountIds.has(account.id!)) {
                await AccountService.unhideAccount(account.id!);
                setHiddenAccountIds(prev => {
                    const next = new Set(prev);
                    next.delete(account.id!);
                    return next;
                });
            } else {
                await AccountService.hideAccount(account.id!);
                setHiddenAccountIds(prev => new Set(prev).add(account.id!));
            }
        } catch (err) {
            toast.showApiError(err, 'Failed to update account visibility');
        }
    };

    const handleOpenFormModal = (account?: Account) => {
        if (account) {
            setSelectedAccount(account);
            setPostingClassTouched(true);
            setFormData({
                code: account.code,
                name: account.name,
                accountType: account.accountType,
                description: account.description || '',
                parentAccountId: account.parentAccountId || '',
                postingClass: account.postingClass ?? '',
                segmentFieldPolicy: account.segmentFieldPolicy ?? 'OPTIONAL',
                cashFlowCategory: account.cashFlowCategory ?? ''
            });
        } else {
            setSelectedAccount(null);
            setPostingClassTouched(false);
            setFormData({
                code: '',
                name: '',
                accountType: 'ASSET',
                description: '',
                parentAccountId: '',
                postingClass: defaultPostingClass('ASSET'),
                segmentFieldPolicy: 'OPTIONAL',
                cashFlowCategory: ''
            });
        }
        setIsFormModalOpen(true);
    };

    const handleCloseFormModal = () => {
        setIsFormModalOpen(false);
        setSelectedAccount(null);
    };

    const handleOpenDeleteModal = (account: Account) => {
        setSelectedAccount(account);
        setIsDeleteModalOpen(true);
    };

    const handleCloseDeleteModal = () => {
        setIsDeleteModalOpen(false);
        setSelectedAccount(null);
    };

    const handleDelete = async () => {
        if (!selectedAccount) return;
        try {
            await AccountService.deleteAccount(selectedAccount.id!);
            toast.showSuccess(`Account ${selectedAccount.code} deleted`);
            await fetchAccounts();
            handleCloseDeleteModal();
        } catch (err) {
            toast.showApiError(err, 'Failed to delete account');
        }
    };

    const parentAccount = accounts.find(a => a.id === formData.parentAccountId);
    // Existing accounts keep whatever code shape they already have (older charts use shorter
    // codes); the 10-digit/parent rules apply to new accounts and to changed codes or parents.
    const codeUnchanged = !!selectedAccount && formData.code === selectedAccount.code
        && (formData.parentAccountId || '') === (selectedAccount.parentAccountId || '');
    const codeError = formData.code && !codeUnchanged ? validateAccountCode(formData.code, parentAccount) : '';

    const handleParentChange = (id: string) => {
        const parent = accounts.find(a => a.id === id);
        const suggestion = parent && !selectedAccount && !formData.code ? suggestNextChildCode(parent, accounts) : '';
        // A new account under a parent defaults to the parent's type (still editable afterwards).
        const accountType = parent && !selectedAccount ? parent.accountType : formData.accountType;
        const postingClass = !selectedAccount && !postingClassTouched
            ? defaultPostingClass(accountType, parent) : formData.postingClass;
        setFormData({
            ...formData, parentAccountId: id, code: suggestion || formData.code, postingClass, accountType,
            segmentFieldPolicy: accountType !== formData.accountType ? deriveSegmentPolicy(accountType) : formData.segmentFieldPolicy,
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const problem = codeUnchanged ? '' : validateAccountCode(formData.code, parentAccount);
        if (problem) {
            toast.showError(problem);
            return;
        }
        try {
            const institutionId = AuthService.getSelectedInstitution();
            const accountData: Account = {
                code: formData.code,
                name: formData.name,
                accountType: formData.accountType,
                description: formData.description || undefined,
                parentAccountId: formData.parentAccountId || undefined,
                institutionId,
                ...(formData.postingClass ? { postingClass: formData.postingClass } : {}),
                segmentFieldPolicy: formData.segmentFieldPolicy,
                cashFlowCategory: formData.cashFlowCategory || null
            };
            if (selectedAccount) {
                await AccountService.updateAccount(selectedAccount.id!, accountData);
                toast.showSuccess(`Account ${formData.code} updated`);
            } else {
                await AccountService.createAccount(accountData);
                toast.showSuccess(`Account ${formData.code} created`);
            }
            await fetchAccounts();
            handleCloseFormModal();
        } catch (err) {
            toast.showApiError(err, 'Failed to save account');
        }
    };

    const displayedAccounts = showHidden
        ? accounts
        : accounts.filter(a => !hiddenAccountIds.has(a.id!));

    const groupedAccounts = ACCOUNT_TYPES.reduce((acc, type) => {
        acc[type] = displayedAccounts
            .filter(a => a.accountType === type)
            .sort((a, b) => a.code.localeCompare(b.code));
        return acc;
    }, {} as Record<string, Account[]>);

    const toggleCollapsed = (code: string) => {
        setCollapsed(prev => {
            const next = new Set(prev);
            if (next.has(code)) next.delete(code); else next.add(code);
            return next;
        });
    };

    const parentCodes = displayedAccounts
        .filter(a => displayedAccounts.some(o => isDescendant(o, a)))
        .map(a => a.code);

    const handleCollapseExpandAll = () => {
        setCollapsed(prev => (prev.size > 0 ? new Set() : new Set(parentCodes)));
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <div style={{ padding: '20px' }} data-help-page="accounting.coa.page">
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                <h2>Chart of Accounts</h2>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    <Button data-help="accounting.coa.expandAll"
                        variant="outlined"
                        color="inherit"
                        onClick={handleCollapseExpandAll}
                        startIcon={collapsed.size > 0 ? <UnfoldMore /> : <UnfoldLess />}
                    >
                        {collapsed.size > 0 ? 'Expand All' : 'Collapse All'}
                    </Button>
                    <Button data-help="accounting.coa.showHidden"
                        variant="outlined"
                        color={showHidden ? 'primary' : 'inherit'}
                        onClick={() => setShowHidden(!showHidden)}
                        startIcon={showHidden ? <Visibility /> : <VisibilityOff />}
                    >
                        {showHidden ? 'Showing All' : 'Show Hidden'}
                    </Button>
                    {canManageAccounts && (
                        <Button data-help="accounting.coa.import" variant="outlined" startIcon={<UploadFile />} onClick={() => setIsImportOpen(true)}>
                            Import CSV
                        </Button>
                    )}
                    {canManageAccounts && (
                        <Button data-help="accounting.coa.add" variant="contained" color="primary" startIcon={<Add />} onClick={() => handleOpenFormModal()}>
                            Add Account
                        </Button>
                    )}
                </div>
            </div>

            {ACCOUNT_TYPES.map(type => (
                <div key={type} style={{ marginBottom: '24px' }}>
                    <h3 style={{ marginBottom: '8px', color: '#333' }}>{humanizeEnum(type)}</h3>
                    <ScrollableTable>
                    <table data-help-section="accounting.coa.table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: isNarrow ? 0 : 640 }}>
                        <thead>
                            <tr>
                                <th data-help="accounting.coa.col.code" style={tableHeaderStyle}>Code</th>
                                {!isNarrow && <th data-help="accounting.coa.col.level" style={{ ...tableHeaderStyle, width: '60px' }}>Level</th>}
                                <th data-help="accounting.coa.col.name" style={{ ...tableHeaderStyle, minWidth: isNarrow ? 160 : undefined }}>Name</th>
                                {!isNarrow && <th data-help="accounting.coa.col.description" style={tableHeaderStyle}>Description</th>}
                                {!isNarrow && <th data-help="accounting.coa.col.postingClass" style={tableHeaderStyle}>Posting Class</th>}
                                <th data-help="accounting.coa.col.actions" style={tableHeaderStyle}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {groupedAccounts[type]?.length === 0 ? (
                                <tr>
                                    <td style={tableCellStyle} colSpan={isNarrow ? 3 : 6}>No accounts in this category</td>
                                </tr>
                            ) : (
                                groupedAccounts[type]?.filter(account =>
                                    !groupedAccounts[type].some(p => collapsed.has(p.code) && isDescendant(account, p))
                                ).map(account => {
                                    const isHidden = hiddenAccountIds.has(account.id!);
                                    const isGlobal = !account.institutionId;
                                    const level = getAccountLevel(account.code);
                                    const indentPx = (level - 1) * (isNarrow ? 10 : 16);
                                    const hasChildren = groupedAccounts[type].some(o => isDescendant(o, account));
                                    const isCollapsed = collapsed.has(account.code);
                                    return (
                                        <tr key={account.id} style={isHidden ? { opacity: 0.5 } : undefined}>
                                            <td style={{ ...tableCellStyle, whiteSpace: 'nowrap' }}>{account.code}</td>
                                            {!isNarrow && <td style={{ ...tableCellStyle, color: '#666', fontSize: '12px', fontWeight: level === 1 ? 700 : 400 }}>L{level}</td>}
                                            <td style={{ ...tableCellStyle, minWidth: isNarrow ? 160 : undefined, paddingLeft: `${hasChildren ? indentPx : 12 + indentPx + 28}px`, fontWeight: level <= 2 ? 600 : 400 }}>
                                                {hasChildren && (
                                                    <IconButton data-help="accounting.coa.action.fold"
                                                        size="small"
                                                        onClick={() => toggleCollapsed(account.code)}
                                                        aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} ${account.code}`}
                                                        sx={{ mr: 0.5 }}
                                                    >
                                                        {isCollapsed ? <ChevronRight fontSize="small" /> : <ExpandMore fontSize="small" />}
                                                    </IconButton>
                                                )}
                                                {account.name}
                                                {isGlobal && (
                                                    <span style={{ marginLeft: '8px', fontSize: '11px', color: '#888', backgroundColor: '#f0f0f0', padding: '2px 6px', borderRadius: '4px' }}>
                                                        System
                                                    </span>
                                                )}
                                            </td>
                                            {!isNarrow && <td style={tableCellStyle}>{account.description || '-'}</td>}
                                            {!isNarrow && <td style={tableCellStyle}>
                                                {account.postingClass
                                                    ? <Chip label={postingClassLabel(account.postingClass)} size="small" />
                                                    : <span style={{ color: '#bbb' }}>—</span>}
                                            </td>}
                                            <td style={{ ...tableCellStyle, whiteSpace: 'nowrap' }}>
                                                <Tooltip title="View Ledger">
                                                    <IconButton data-help="accounting.coa.ledger" size="small" onClick={() => navigate(`/accounting/accounts/${account.id}/ledger`)} color="primary" aria-label="view ledger">
                                                        <Summarize />
                                                    </IconButton>
                                                </Tooltip>
                                                {canManageAccounts && <Tooltip title={isHidden ? 'Show account' : 'Hide account'}>
                                                    <IconButton data-help="accounting.coa.hide" size="small" onClick={() => handleToggleVisibility(account)} color={isHidden ? 'default' : 'primary'} aria-label={isHidden ? 'Show account' : 'Hide account'}>
                                                        {isHidden ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </Tooltip>}
                                                {!isGlobal && (
                                                    <>
                                                        {canManageAccounts && (
                                                            <IconButton data-help="accounting.coa.edit" size="small" onClick={() => handleOpenFormModal(account)} color="primary" aria-label="edit">
                                                                <Edit />
                                                            </IconButton>
                                                        )}
                                                        {canDeleteAccounts && (
                                                            <IconButton data-help="accounting.coa.delete" size="small" onClick={() => handleOpenDeleteModal(account)} color="error" aria-label="delete">
                                                                <Delete />
                                                            </IconButton>
                                                        )}
                                                    </>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                    </ScrollableTable>
                </div>
            ))}

            {/* Form Modal */}
            <Modal open={isFormModalOpen} onClose={handleCloseFormModal}>
                <div data-help-page="accounting.coa.form.page" style={modalStyle}>
                    <h2>{selectedAccount ? 'Edit Account' : 'Add Account'}</h2>
                    <form onSubmit={handleSubmit}>
                        <TextField data-help="accounting.coa.form.field.code"
                            fullWidth
                            label="Code"
                            value={formData.code}
                            onChange={(e) => setFormData({ ...formData, code: e.target.value.trim() })}
                            required
                            margin="normal"
                            error={!!codeError}
                            helperText={codeError || '10 digits, e.g. 1001010100. Pick a parent first to get the next free code.'}
                            inputProps={{ inputMode: 'numeric', maxLength: 10 }}
                        />
                        <TextField data-help="accounting.coa.form.field.name"
                            fullWidth
                            label="Name"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            required
                            margin="normal"
                        />
                        <FormControl fullWidth margin="normal" required>
                            <InputLabel>Account Type</InputLabel>
                            <Select data-help="accounting.coa.form.field.accountType"
                                data-testid="account-type-select"
                                value={formData.accountType}
                                label="Account Type"
                                onChange={(e) => {
                                    const accountType = e.target.value as Account['accountType'];
                                    const postingClass = !selectedAccount && !postingClassTouched
                                        ? defaultPostingClass(accountType, parentAccount) : formData.postingClass;
                                    setFormData({ ...formData, accountType, segmentFieldPolicy: deriveSegmentPolicy(accountType), postingClass });
                                }}
                            >
                                {ACCOUNT_TYPES.map(type => (
                                    <MenuItem key={type} value={type}>{humanizeEnum(type)}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField data-help="accounting.coa.form.field.description"
                            fullWidth
                            label="Description"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            margin="normal"
                            multiline
                            rows={3}
                        />
                        <AccountAutocomplete data-help="accounting.coa.form.field.parent"
                            accounts={accounts.filter(a => a.id !== selectedAccount?.id)}
                            value={formData.parentAccountId}
                            onChange={handleParentChange}
                            label="Parent Account (Optional)"
                            placeholder="None"
                            size="medium"
                        />
                        <FormControl fullWidth margin="normal">
                            <InputLabel id="account-posting-class-label">Posting Class</InputLabel>
                            <Select data-help="accounting.coa.form.field.postingClass"
                                labelId="account-posting-class-label"
                                value={formData.postingClass}
                                label="Posting Class"
                                onChange={(e) => {
                                    setPostingClassTouched(true);
                                    setFormData({ ...formData, postingClass: e.target.value as PostingClass | '' });
                                }}
                            >
                                <MenuItem value="">(none)</MenuItem>
                                {POSTING_CLASS_OPTIONS.map(pc => (
                                    <MenuItem key={pc} value={pc}>{postingClassLabel(pc)}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth margin="normal">
                            <InputLabel id="account-segment-policy-label">Segment Policy</InputLabel>
                            <Select data-help="accounting.coa.form.field.segmentPolicy"
                                labelId="account-segment-policy-label"
                                data-testid="account-segment-policy"
                                value={formData.segmentFieldPolicy}
                                label="Segment Policy"
                                onChange={(e) => setFormData({ ...formData, segmentFieldPolicy: e.target.value as SegmentFieldPolicy })}
                            >
                                <MenuItem value="OPTIONAL">Optional</MenuItem>
                                <MenuItem value="REQUIRED">Required</MenuItem>
                            </Select>
                        </FormControl>
                        {formData.accountType !== 'REVENUE' && formData.accountType !== 'EXPENSE' && (
                            <FormControl fullWidth margin="normal">
                                <InputLabel id="account-cash-flow-label">Cash Flow Category</InputLabel>
                                <Select data-help="accounting.coa.form.field.cashFlow"
                                    labelId="account-cash-flow-label"
                                    data-testid="account-cash-flow-category"
                                    value={formData.cashFlowCategory}
                                    label="Cash Flow Category"
                                    onChange={(e) => setFormData({ ...formData, cashFlowCategory: e.target.value as CashFlowCategory | '' })}
                                >
                                    <MenuItem value="">Automatic (derived)</MenuItem>
                                    {CASH_FLOW_CATEGORY_OPTIONS.map(c => (
                                        <MenuItem key={c} value={c}>{humanizeEnum(c)}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                            <Button data-help="accounting.coa.form.cancel" onClick={handleCloseFormModal}>Cancel</Button>
                            <Button data-help="accounting.coa.form.save" type="submit" variant="contained" color="primary" disabled={!!codeError}>
                                {selectedAccount ? 'Update' : 'Create'}
                            </Button>
                        </div>
                    </form>
                </div>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal open={isDeleteModalOpen} onClose={handleCloseDeleteModal}>
                <div data-help-page="accounting.coa.delete.page" style={modalStyle}>
                    <h2>Delete Account</h2>
                    <p>Are you sure you want to delete account "{selectedAccount?.name}"?</p>
                    <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                        <Button data-help="accounting.coa.delete.cancel" onClick={handleCloseDeleteModal}>Cancel</Button>
                        <Button data-help="accounting.coa.delete.confirm" onClick={handleDelete} variant="contained" color="error">Delete</Button>
                    </div>
                </div>
            </Modal>
            {canManageAccounts && (
                <CoaImportDialog open={isImportOpen} onClose={() => setIsImportOpen(false)} onImported={fetchAccounts} />
            )}
        </div>
    );
};

export default ChartOfAccounts;
